import { get, post, put } from "aws-amplify/api";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Alert, Col, Row } from "react-bootstrap";
import Select from "react-select";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createSLMCategoriesElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

const columnHelper = createColumnHelper();

function normalizeGroupOptions(groups) {
  if (!Array.isArray(groups)) {
    return [];
  }

  return groups
    .map((group) => {
      const value = group?.value ?? group?.group_id ?? group?.id ?? null;
      const label = group?.label ?? group?.group_name ?? group?.name ?? null;

      if (value === null || label === null) {
        return null;
      }

      return { value: String(value), label: String(label) };
    })
    .filter(Boolean);
}

function SLMCategoriesList({ isAuthenticated, user, categoryGroups = [] }) {
  const slmCategoriesIdPrefix = "slm-categories-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const createId = useMemo(
    () => createElementIdFactory(slmCategoriesIdPrefix),
    [slmCategoriesIdPrefix]
  );

  const tableElementIds = useMemo(
    () => createSLMCategoriesElementIds(createId),
    [createId]
  );

  const fallbackGroupOptions = useMemo(() => {
    const mapped = categories
      .map((record) => {
        if (!record?.group_id || !record?.group_name) {
          return null;
        }

        return {
          value: String(record.group_id),
          label: String(record.group_name),
        };
      })
      .filter(Boolean);

    const deduped = [];
    const seen = new Set();

    mapped.forEach((opt) => {
      const key = `${opt.value}::${opt.label}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(opt);
      }
    });

    return deduped;
  }, [categories]);

  const groupOptions = useMemo(() => {
    const normalized = normalizeGroupOptions(categoryGroups);
    return normalized.length > 0 ? normalized : fallbackGroupOptions;
  }, [categoryGroups, fallbackGroupOptions]);

  const filteredCategories = useMemo(() => {
    if (!selectedGroup?.value) {
      return categories;
    }

    return categories.filter(
      (record) => String(record?.group_id ?? "") === String(selectedGroup.value)
    );
  }, [categories, selectedGroup]);

  const getRecordKey = useCallback((record) => {
    if (record?.category_id !== undefined && record?.category_id !== null) {
      return `id-${record.category_id}`;
    }
    return `tmp-${record?._clientId}`;
  }, []);

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await get({
        apiName: "colin",
        path: "/slmcategories",
      }).response;

      const json = await response.body.json();
      const withClientIds = (json || []).map((record) => ({
        ...record,
        _clientId: record?._clientId ?? `existing-${record.category_id}`,
      }));

      setCategories(withClientIds);
      setUi({ showAlert: false, errorMessage: "" });
      document.title = "Categories lookup";
    } catch (error) {
      console.error("Failed to fetch SLM categories", error);
      setUi({ showAlert: true, errorMessage: "Failed to load categories." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    fetchCategories();
  }, [isAuthenticated, fetchCategories]);

  const saveRecord = useCallback(
    async (record) => {
      const isNew = Boolean(record.new_record || !record.category_id);

      const path = isNew
        ? "/slmcategories/new"
        : `/slmcategories/${record.category_id}`;

      const method = isNew ? post : put;

      const response = await method({
        apiName: "colin",
        path,
        options: {
          body: {
            ...record,
            last_edited_by: user,
          },
        },
      }).response;

      return response.body.json();
    },
    [user]
  );

  const updateRecordByIndex = useCallback(
    (rowIndex, updateFn) => {
      const rowRecord = filteredCategories[rowIndex];

      if (!rowRecord) {
        return;
      }

      const targetKey = getRecordKey(rowRecord);

      setCategories((previous) =>
        previous.map((record) => {
          if (getRecordKey(record) !== targetKey) {
            return record;
          }

          const updatedRecord = typeof updateFn === "function" ? updateFn(record) : record;
          return updatedRecord;
        })
      );
    },
    [filteredCategories, getRecordKey]
  );

  const updateTableState = useCallback(
    (rowIndex, columnId, value) => {
      updateRecordByIndex(rowIndex, (record) => {
        const next = {
          ...record,
          [columnId]: value,
          modified: true,
        };

        if (columnId === "group_id") {
          const selected = groupOptions.find((opt) => String(opt.value) === String(value));
          if (selected) {
            next.group_name = selected.label;
          }
        }

        return next;
      });
    },
    [groupOptions, updateRecordByIndex]
  );

  const handleSave = useCallback(
    async ({ row, column, value }) => {
      const updatedRecord = {
        ...row.original,
        [column.id]: value,
      };

      if (column.id === "group_id") {
        const selected = groupOptions.find((opt) => String(opt.value) === String(value));
        if (selected) {
          updatedRecord.group_name = selected.label;
        }
      }

      if (!updatedRecord.group_id && updatedRecord.group_id !== 0) {
        setUi({
          showAlert: true,
          errorMessage: "You must select a group. The record has not been saved.",
        });
        return false;
      }

      updateRecordByIndex(row.index, (record) => ({ ...record, isSaving: true }));

      try {
        const result = await saveRecord(updatedRecord);

        if (!result?.result) {
          setUi({
            showAlert: true,
            errorMessage: result?.error_message || "Failed to save the record.",
          });

          updateRecordByIndex(row.index, (record) => ({ ...record, isSaving: false }));
          return false;
        }

        updateRecordByIndex(row.index, (record) => ({
          ...record,
          ...updatedRecord,
          category_id: result.id ?? record.category_id,
          modified: false,
          new_record: false,
          isSaving: false,
        }));

        setUi({ showAlert: false, errorMessage: "" });
        return true;
      } catch (error) {
        console.error("Failed to save SLM category", error);
        setUi({
          showAlert: true,
          errorMessage: "Network Error: failed to save changes.",
        });

        updateRecordByIndex(row.index, (record) => ({ ...record, isSaving: false }));
        return false;
      }
    },
    [groupOptions, saveRecord, updateRecordByIndex]
  );

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("category_id", {
        id: "category_id",
        header: "ID",
        cell: (info) => info.getValue() ?? "",
        size: 90,
      }),
      columnHelper.accessor("code", {
        id: "code",
        header: "Code",
        cell: (info) => info.getValue() ?? "",
        size: 140,
      }),
      columnHelper.accessor("name", {
        id: "name",
        header: "Name",
        cell: (info) => info.getValue() ?? "",
        size: 240,
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "Description",
        cell: (info) => info.getValue() ?? "",
        size: 360,
      }),
      columnHelper.accessor("group_id", {
        id: "group_id",
        header: "Group",
        cell: (info) => info.getValue() ?? info.row.original?.group_id ?? "",
        size: 220,
      }),
    ];
  }, []);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="mt-3">
      <Alert
        show={ui.showAlert}
        variant="danger"
        onClose={() => setUi((previous) => ({ ...previous, showAlert: false }))}
        dismissible
      >
        {ui.errorMessage}
      </Alert>

      <Row>
        <Col md={5} lg={4} style={{ marginBottom: "12px" }}>
          <Select
            value={selectedGroup}
            onChange={setSelectedGroup}
            options={groupOptions}
            isClearable
            placeholder="Filter by group"
            inputId={tableElementIds.groupFilter}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={filteredCategories}
                section="slm-categories-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.slmLookupCategories}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                getRowId={(row) => String(row.category_id ?? row._clientId)}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default SLMCategoriesList;